import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import moment from 'moment-timezone';
import { groupPurchaseStatus } from '@silvergatedelivery/constants';

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    padding: theme.spacing(2),
    marginBottom: 12,
  },
  mealTypeContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    width: '100%',
  },
  mealTypeTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  mealTypeLabel: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  mealTypeCount: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  mealStatusContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
  },
  mealStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  mealStatusLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  mealStatusCount: {
    marginRight: theme.spacing(1),
  },
  acceptedCount: {
    color: groupPurchaseStatus.find(({ value }) => value === 'accepted').colorText,
  },
  abandonedCount: {
    color: groupPurchaseStatus.find(({ value }) => value === 'abandoned').colorText,
  },
  pendingCount: {
    color: groupPurchaseStatus.find(({ value }) => value === 'pending').colorText,
  },
  clientCancelledCount: {
    color: groupPurchaseStatus.find(({ value }) => value === 'clientCancelled').colorText,
  },
}));

const MealTypeDisplay = ({
  title, acceptedCount, abandonedCount, pendingCount, clientCancelledCount, minMealCount, maxMealCount,
}) => {
  const classes = useStyles();

  const count = acceptedCount + pendingCount;

  return (
    <Box className={classes.mealTypeContainer}>
      <Box className={classes.mealTypeTitle}>
        <Typography variant="subtitle2" className={classes.mealTypeLabel}>
          {title}
        </Typography>
        <Typography variant="h6" className={classes.mealTypeCount}>
          {count} 份
        </Typography>
        {clientCancelledCount > 0 && (
          <Box display="flex" alignItems="center" ml={3}>
            <Typography className={classes.mealStatusLabel}>
              {groupPurchaseStatus.find(({ value }) => value === 'clientCancelled').label}
            </Typography>
            <Typography className={`${classes.mealStatusCount} ${classes.clientCancelledCount}`}>
              {clientCancelledCount}
            </Typography>
          </Box>
        )}
      </Box>
      <Box className={classes.mealStatusContainer}>
        <Box className={classes.mealStatus}>
          <Typography className={classes.mealStatusLabel}>
            {groupPurchaseStatus.find(({ value }) => value === 'accepted').label}
          </Typography>
          <Typography className={`${classes.mealStatusCount} ${classes.acceptedCount}`}>
            {acceptedCount}
          </Typography>
        </Box>
        <Box className={classes.mealStatus}>
          <Typography className={classes.mealStatusLabel}>
            {groupPurchaseStatus.find(({ value }) => value === 'pending').label}
          </Typography>
          <Typography className={`${classes.mealStatusCount} ${classes.pendingCount}`}>
            {pendingCount}
          </Typography>
        </Box>
        <Box className={classes.mealStatus}>
          <Typography className={classes.mealStatusLabel}>
            {groupPurchaseStatus.find(({ value }) => value === 'abandoned').label}
          </Typography>
          <Typography className={`${classes.mealStatusCount} ${classes.abandonedCount}`}>
            {abandonedCount}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: '100%', mt: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min((count / maxMealCount) * 100, 100)}
          color={(count !== 0 && count >= minMealCount) ? 'primary' : 'secondary'}
        />
      </Box>
    </Box>
  );
};

MealTypeDisplay.propTypes = {
  title: PropTypes.string.isRequired,
  acceptedCount: PropTypes.number.isRequired,
  abandonedCount: PropTypes.number.isRequired,
  pendingCount: PropTypes.number.isRequired,
  clientCancelledCount: PropTypes.number.isRequired,
  minMealCount: PropTypes.number.isRequired,
  maxMealCount: PropTypes.number.isRequired,
};

export default function OrderOverview({
  ordersByDate,
  targetDate,
  setTargetDate,
  startDate,
  setCustomPeriod,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  let orderCountPerPage;
  if (isXs) {
    orderCountPerPage = 1;
  } else if (isSm) {
    orderCountPerPage = 2;
  } else if (isMd) {
    orderCountPerPage = 3;
  } else if (isLg) {
    orderCountPerPage = 4;
  } else if (isXl) {
    orderCountPerPage = 5;
  } else {
    orderCountPerPage = 3;
  }

  const endDate = moment(startDate).add(orderCountPerPage - 1, 'day').format('YYYY-MM-DD');

  const getMealCount = (orders) => {
    return orders.reduce((acc, order) => {
      order.mealItems.forEach((mealItem) => {
        acc += mealItem.quantity;
      });
      return acc;
    }, 0);
  };

  const getMealCountByStatus = (orders, status) => {
    return orders.filter((order) => order.groupPurchaseStatus === status).reduce((acc, order) => {
      order.mealItems.forEach((mealItem) => {
        acc += mealItem.quantity;
      });
      return acc;
    }, 0);
  };

  return (
    <Box className={classes.filterContainer}>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={1}>
              <Button
                onClick={() => {
                  const newStartDate = moment(startDate).subtract(orderCountPerPage, 'day').format('YYYY-MM-DD');
                  const newEndDate = moment(endDate).subtract(orderCountPerPage, 'day').format('YYYY-MM-DD');
                  setCustomPeriod({ startDate: newStartDate, endDate: newEndDate });
                }}
                style={{ width: '100%' }}
              >
                <Paper
                  elevation={3}
                  style={{
                    padding: 16,
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle1">
                    {`往前${orderCountPerPage}天`}
                  </Typography>
                </Paper>
              </Button>
            </Grid>
            <Grid item container xs={10} spacing={1}>
              {Object.entries(ordersByDate).map(([date, orders], index) => {
                const pendingLunchCount = getMealCountByStatus(orders.lunch, 'pending');
                const pendingDinnerCount = getMealCountByStatus(orders.dinner, 'pending');
                const acceptedLunchCount = getMealCountByStatus(orders.lunch, 'accepted');
                const acceptedDinnerCount = getMealCountByStatus(orders.dinner, 'accepted');
                const abandonedLunchCount = getMealCountByStatus(orders.lunch, 'abandoned');
                const abandonedDinnerCount = getMealCountByStatus(orders.dinner, 'abandoned');
                const clientCancelledLunchCount = getMealCountByStatus(orders.lunch, 'clientCancelled');
                const clientCancelledDinnerCount = getMealCountByStatus(orders.dinner, 'clientCancelled');
                const lunchCount = getMealCount(orders.lunch) - clientCancelledLunchCount;
                const dinnerCount = getMealCount(orders.dinner) - clientCancelledDinnerCount;
                const minLunchMealCount = lunchCount ? orders.lunch[0].restaurant.minOrderQuantity || 0 : 0;
                const maxLunchMealCount = lunchCount ? orders.lunch[0].restaurant.maxOrderQuantity || lunchCount * 2 : 1;
                const minDinnerMealCount = dinnerCount ? orders.dinner[0].restaurant.minOrderQuantity || 0 : 0;
                const maxDinnerMealCount = dinnerCount ? orders.dinner[0].restaurant.maxOrderQuantity || dinnerCount * 2 : 1;

                if (index >= orderCountPerPage) {
                  return null;
                }

                return (
                  <Grid item xs key={date}>
                    <Button
                      onClick={() => setTargetDate(date)}
                      style={{
                        width: '100%',
                      }}
                    >
                      <Paper
                        elevation={3}
                        style={{
                          padding: 16,
                          textAlign: 'center',
                          width: '100%',
                          border: targetDate === date ? '2px solid #1976d2' : 'none',
                        }}
                      >
                        <Typography variant="subtitle1">
                          {moment(date).format('MM/DD dddd')}
                        </Typography>
                        <MealTypeDisplay
                          title="午餐"
                          acceptedCount={acceptedLunchCount}
                          abandonedCount={abandonedLunchCount}
                          pendingCount={pendingLunchCount}
                          clientCancelledCount={clientCancelledLunchCount}
                          minMealCount={minLunchMealCount}
                          maxMealCount={maxLunchMealCount}
                        />
                        <MealTypeDisplay
                          title="晚餐"
                          acceptedCount={acceptedDinnerCount}
                          abandonedCount={abandonedDinnerCount}
                          pendingCount={pendingDinnerCount}
                          clientCancelledCount={clientCancelledDinnerCount}
                          minMealCount={minDinnerMealCount}
                          maxMealCount={maxDinnerMealCount}
                        />
                      </Paper>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
            <Grid item xs={1}>
              <Button
                onClick={() => {
                  const newStartDate = moment(startDate).add(orderCountPerPage, 'day').format('YYYY-MM-DD');
                  const newEndDate = moment(endDate).add(orderCountPerPage, 'day').format('YYYY-MM-DD');
                  setCustomPeriod({ startDate: newStartDate, endDate: newEndDate });
                }}
                style={{ width: '100%' }}
              >
                <Paper
                  elevation={3}
                  style={{
                    padding: 16,
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle1">
                    {`往後${orderCountPerPage}天`}
                  </Typography>
                </Paper>
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

OrderOverview.propTypes = {
  ordersByDate: PropTypes.object.isRequired,
  targetDate: PropTypes.string.isRequired,
  setTargetDate: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  setCustomPeriod: PropTypes.func.isRequired,
};
